import { Decoration, DecorationSet } from 'prosemirror-view';
import { detect } from 'detect-browser';
import { Plugin, PluginKey } from 'prosemirror-state';
import crel from 'crel';

const browser = detect();

/** Searches a resolved node for a group of text nodes containing the specifed
 * mark which the mouse is over
 */
function findLinkCenter(mousePos, mark, getKey) {
   const node = mousePos.parent;

   let currKey = null;
   let link = [];
   let curr = mousePos.start(mousePos.depth);
   let start = curr;

   function resolvePosition() {
      if (link.length > 0) {
         if (mousePos.pos >= start && mousePos.pos <= curr) {
            // We found the link
            // We can safely get the first item, because we know there's
            // at least one.
            const key = getKey(link[0].marks.find(m => m.type.name === mark.name));
            return { start, end: curr, link, key };
         }
      }
   }

   for (let i = 0; i < node.childCount; i++) {
      const n = node.child(i);

      let newLink = null;
      const marked = n.marks.find(m => m.type.name === mark.name);
      if (marked) {
         if (getKey(marked) === currKey) {
            // Test case: [this is **link text** that has different
            // styles](dest)[ followed by more text from a different
            // link](another dest)
            link.push(n);
         } else {
            currKey = getKey(marked);
            newLink = [n];
         }
      } else {
         currKey = null;
         newLink = [];
      }
      if (newLink) {
         const position = resolvePosition();
         if (position) {
            return position; // We found what we were looking for.
         }
         link = newLink;
         start = curr;
      }
      curr += n.nodeSize;
   }

   return resolvePosition();
}

/**
 * Modify position of `popup` to ensure it remains entirely within boundary
 * while being as close to `cursorBox` as possible.
 */
function centerPopup(boundary, cursorBox, popup) {
   const setVisible = function(el) {
      if (el.children) {
         Array.convert(el.children).forEach(setVisible);
      }
      el.style.visibility = 'visible';
   };

   const setHidden = function(el) {
      if (el.children) {
         Array.convert(el.children).forEach(setVisible);
      }
      el.style.visibility = 'hidden';
   };

   const parent = popup.parentElement;
   if (!parent) {
      // If the popup has no parent, it isn't in the DOM yet. Throw it back on
      // the event loop and try again.
      setHidden(popup);
      window.setTimeout(setVisible(popup));
   } else {
      setVisible(popup);
   }
}

class Dispatcher {
   constructor() {
      this.pending = null;
      this.pendingPriority = 0;
   }

   start(priority, view, action, time = 0) {
      if (this.pendingPriority < priority) {
         this.cancel();
         this.pending = window.setTimeout(() => {
            action();
            this.pending = null;
            this.pendingPriority = 0;
         }, time);
         this.pendingPriority = priority;
      }
   }

   cancel() {
      if (this.pending) {
         window.clearTimeout(this.pending);
         this.pending = null;
         this.pendingPriority = 0;
      }
   }
}

function getPopup(view, plugin) {
   const cancelButton = crel(
      'button',
      {
         class: 'button button-action link-edit-button',
         title: _js('Cancel (Esc)'),
      },
      _js('Cancel')
   );

   const deleteButton = crel(
      'button',
      {
         class: 'button link-edit-button',
         title: _js('Remove'),
      },
      _js('Remove')
   );

   const confirmButton = crel(
      'button',
      {
         class: 'button button-action-solid link-edit-button',
         title: _js('Confirm (Enter)'),
      },
      _js('Confirm Link')
   );

   const buttons = crel(
      'div',
      { class: 'link-edit-buttongroup' },
      deleteButton,
      cancelButton,
      confirmButton
   );

   const input = crel('input', {
      type: 'text',
      class: 'js-link-editor',
      placeholder: 'URL',
   });

   const popup = crel('div', { class: 'prosemirror-link-editor' }, input, buttons);

   const events = [
      'keydown',
      'keypress',
      'input',
      'keyup',
      'mousedown',
      'mouseup',
      'click',
      'dblclick',
      'cut',
      'copy',
      'paste',
   ];
   events.forEach(e => {
      input.addEventListener(e, ev => ev.stopPropagation());
   });

   input.addEventListener('keydown', ev => {
      if (ev.key === 'Enter' || ev.key === 'Escape') {
         ev.preventDefault();
      }
   });

   return { popup, input, cancelButton, confirmButton, deleteButton };
}

function linkEqual(left, right) {
   if (left.length === right.length) {
      for (let i = 0; i < left.length; i++) {
         if (left[i] !== right[i]) {
            return false;
         }
      }
      return true;
   }
   return false;
}

/**
 * linkEditor creates a plugin that makes the href of link Marks editable
 * through a popup textbox when they are moused over.
 */
export default function linkEditor(container) {
   const dispatcher = new Dispatcher();
   const overrideEditingLock = 'override_editor_lock';

   function commit(view, plugin, href) {
      const schema = view.state.schema;
      const state = plugin.getState(view.state);
      const markType = schema.marks.link;
      const hasPreceedingProtocolSpecifier = href => /^[a-z][a-z0-9+.-]*:\/\//i.test(href);

      if (state.info && href !== state.info.href) {
         if (href && !hasPreceedingProtocolSpecifier(href)) {
            href = 'http://' + href;
         }

         const linkMark = schema.mark('link', { href });
         let tr = view.state.tr;
         if (href) {
            tr.addMark(state.info.start, state.info.end, linkMark);
         } else {
            tr.removeMark(state.info.start, state.info.end, markType);
         }
         view.dispatch(tr.setMeta(overrideEditingLock, true));
      }
   }

   function saveAndSetMetaDelayed(view, plugin, priority, meta, time = 0) {
      const state = plugin.getState(view.state);
      if (state.info && state.info.commit) {
         state.info.commit(view, plugin);
      }
      function doOnDispatch() {
         view.dispatch(view.state.tr.setMeta(plugin, meta));
      }
      dispatcher.start(priority, view, doOnDispatch, time);
   }

   function clearPopupWithoutSaving(view, plugin) {
      const url = view.state[plugin.key].info.key;
      const input = view.state[plugin.key].info.input;

      input.firstChild.value = url;
      clearPopup(view, plugin, 1);
   }

   function clearPopup(view, plugin, priority, time = 0) {
      saveAndSetMetaDelayed(view, plugin, priority, { action: 'clear' }, time);
      view.focus();
   }

   function showPopup(view, plugin, info) {
      saveAndSetMetaDelayed(view, plugin, 3, { action: 'show', info });
   }

   const key = new PluginKey('link_editor');

   return new Plugin({
      key,
      props: {
         decorations: function(editorState) {
            const state = this.getState(editorState);
            if (state.state === 'visible') {
               const info = state.info;
               const cursorBox = info.leftEdgeCoords;

               centerPopup(container.getBoundingClientRect(), cursorBox, info.input);

               const pointer = crel('div', { class: 'widget-container' }, info.input);
               const widget = Decoration.widget(info.leftEdge, pointer);

               return DecorationSet.create(editorState.doc, [widget]);
            } else {
               return DecorationSet.empty;
            }
         },
         handleDOMEvents: {
            click: function(view, ev) {
               const coords = { left: ev.clientX, top: ev.clientY };
               const position = view.posAtCoords(coords);

               if (this.getState(view.state).state !== 'hidden') {
                  ev.preventDefault();
                  ev.stopImmediatePropagation();
                  const url = view.state[this.key].info.key;
                  const input = view.state[this.key].info.input;

                  input.firstChild.value = url;
                  clearPopup(view, this, 1);
               } else if (position !== null) {
                  this.props.showEditingBubble(view, position.pos);
                  this.props.focusEditingBubble(view);
               }
            },
         },
         hideEditingBubble: function(view) {
            if (this.getState(view.state).state !== 'hidden') {
               clearPopup(view, this, 1, 0);
            }
         },
         showEditingBubble: function(view, pos) {
            const schema = view.state.schema;

            if (pos) {
               const resolv = view.state.doc.resolve(pos);
               const isText = n => n && n.isText;

               // NOTE: This breaks in IE11

               // Require that we be pointing in the middle of text, and that at
               // least the left side is a link.
               if (
                  isText(resolv.nodeBefore) &&
                  isText(resolv.nodeAfter) &&
                  (schema.marks.link.isInSet(resolv.nodeBefore.marks) ||
                     schema.marks.link.isInSet(resolv.nodeAfter.marks))
               ) {
                  const info = findLinkCenter(resolv, schema.marks.link, link => link.attrs.href);
                  if (info) {
                     // Calculate `center` here because we can use `view` to also
                     // calculate the position things will end up at.
                     const leftEdge = info.start;
                     info.leftEdge = leftEdge;
                     info.leftEdgeCoords = view.coordsAtPos(leftEdge);

                     const { popup, input, cancelButton, confirmButton, deleteButton } = getPopup(
                        view,
                        this
                     );
                     if (!info.key) {
                        deleteButton.destroy();
                     }

                     const closeOnClickOut = e => {
                        if (!popup.contains(e.target)) {
                           clearPopupWithoutSaving(view, this);
                        }
                     };

                     // This needs to wait long enough to not catch the click event on the link button.
                     window.setTimeout(
                        () => window.addEventListener('click', closeOnClickOut),
                        100
                     );
                     popup.addEventListener('DOMNodeRemoved', function() {
                        window.removeEventListener('click', closeOnClickOut);
                     });
                     input.addEventListener('keydown', ev => {
                        // Safari <10.1 does not implement KeyboardEvent.key
                        const key = ev.key || ev.keyIdentifier;
                        if (key === 'Enter') {
                           clearPopup(view, this, 1, 0);
                        } else if (key === 'Escape') {
                           clearPopupWithoutSaving(view, this);
                        }
                     });
                     confirmButton.addEventListener('click', ev => {
                        ev.preventDefault();
                        ev.stopImmediatePropagation();
                        clearPopup(view, this, 1);
                     });

                     cancelButton.addEventListener('click', ev => {
                        ev.preventDefault();
                        ev.stopImmediatePropagation();
                        clearPopupWithoutSaving(view, this);
                     });

                     deleteButton.addEventListener('click', ev => {
                        ev.preventDefault();
                        ev.stopImmediatePropagation();
                        input.value = '';
                        clearPopup(view, this, 1);
                     });

                     input.value = info.key;
                     info.commit = view => {
                        commit(view, this, input.value);
                     };
                     info.input = popup;

                     const currState = this.getState(view.state);
                     // Only display new popup if the link has changed
                     if (currState.info) {
                        const link = currState.info.link;
                        if (linkEqual(link, info.link)) {
                           return;
                        }
                     }
                     showPopup(view, this, info);
                     return;
                  }
               }
            }
            const currState = this.getState(view.state);
            if (currState.state === 'visible') {
               if (document.activeElement !== currState.info.input.firstChild) {
                  clearPopup(view, this, 1, 250);
               }

               const coords = view.coordsAtPos(pos);
               const pointedElement = document.elementFromPoint(coords.left, coords.top);
               if (pointedElement === currState.info.input.firstChild) {
                  dispatcher.cancel();
                  return;
               }
            }
         },
         focusEditingBubble: function(view) {
            setTimeout(() => {
               const state = this.getState(view.state);
               if (state.state !== 'hidden') {
                  if (state.info) {
                     state.info.input.firstChild.focus();
                  }
               }
            }, 50);
         },
         handlePaste: function(view, ev, slice) {
            if (browser.name === 'ie') {
               const state = this.getState(view.state);

               if (state.state !== 'hidden') {
                  if (state.info) {
                     const nodes = slice.content.content;
                     const sliceText = nodes.reduce((text, node) => (text += node.textContent), '');

                     state.info.input.firstChild.value = sliceText;
                     state.info.input.firstChild.focus();
                  }

                  return true;
               }
            }
         },
      },
      state: {
         init() {
            return { state: 'hidden' };
         },
         apply(tr, value, oldState, newState) {
            const meta = tr.getMeta(this);
            if (meta) {
               if (meta.action === 'show') {
                  return { state: 'visible', info: meta.info };
               }
               if (meta.action === 'clear') {
                  return { state: 'hidden' };
               }
            }
            if (value.state === 'visible') {
               if (newState.doc.textContent.length === 0) {
                  // If there's no text in the editor, there's no link to edit.
                  return { state: 'autohidden' };
               }
            }

            return value;
         },
      },
      filterTransaction: function(tr, state) {
         const isLinkEditorMetaCommand = tr.meta[this.key];
         const pluginState = this.getState(state).state;
         if (
            pluginState === 'visible' &&
            !tr.getMeta(overrideEditingLock) &&
            !isLinkEditorMetaCommand
         ) {
            return false;
         }

         return true;
      },
   });
}
